/**
 * WarmNodePool API endpoints
 *
 * For static export, these endpoints call the backend API directly
 * using the /api/v1/ prefix. Status values are normalized client-side.
 */
import {
  WarmPool,
  WarmPoolActionResponse,
  WarmPoolCreateData,
  WarmPoolListParams,
  WarmPoolListResponse,
  WarmPoolMode,
  WarmPoolUpdateData,
} from '@/features/warmpools/types/warmpool';
import apiClient, { QueryParams } from './client';

// Base URL for warm node pools API - single source of truth
export const WARM_POOLS_API_BASE = '/api/v1/warmnodepools';

/**
 * Normalize status to match expected format (capitalize first letter)
 * Backend may return lowercase status like "active", UI expects "Active"
 */
function normalizeStatus(status: string): string {
  if (!status) return status;
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

/**
 * Normalize warm pool data from backend.
 * - Capitalises status (e.g. "active" → "Active")
 */
function normalizeWarmPool(pool: WarmPool): WarmPool {
  return {
    ...pool,
    status: normalizeStatus(pool.status) as WarmPool['status'],
  };
}

export const warmPoolsApi = {
  /**
   * List all warm node pools with optional filtering and pagination
   */
  list: async (params?: WarmPoolListParams): Promise<WarmPoolListResponse> => {
    const response = await apiClient.get<WarmPoolListResponse>(WARM_POOLS_API_BASE, {
      params: params as QueryParams,
    });
    return {
      ...response,
      items: Array.isArray(response.items) ? response.items.map(normalizeWarmPool) : response.items,
    };
  },

  /**
   * Get a single warm node pool by name
   */
  get: async (name: string): Promise<WarmPool> => {
    const response = await apiClient.get<WarmPool>(`${WARM_POOLS_API_BASE}/${name}`);
    return normalizeWarmPool(response);
  },

  /**
   * Create a new warm node pool
   */
  create: (data: WarmPoolCreateData): Promise<WarmPoolActionResponse> => {
    return apiClient.post(WARM_POOLS_API_BASE, data);
  },

  /**
   * Update an existing warm node pool
   */
  update: (name: string, data: WarmPoolUpdateData): Promise<WarmPoolActionResponse> => {
    return apiClient.put(`${WARM_POOLS_API_BASE}/${name}`, data);
  },

  /**
   * Delete a warm node pool
   * @param name - Pool name
   * @param force - Force delete even if QueryServices are attached (default: false)
   */
  delete: (name: string, force = false): Promise<WarmPoolActionResponse> => {
    const params: QueryParams = {};
    if (force) {
      params.force = 'true';
    }
    return apiClient.delete(`${WARM_POOLS_API_BASE}/${name}`, { params });
  },

  /**
   * Suspend a warm node pool
   */
  suspend: (name: string): Promise<WarmPoolActionResponse> => {
    return apiClient.post(`${WARM_POOLS_API_BASE}/${name}/suspend`);
  },

  /**
   * Resume a warm node pool
   */
  resume: (name: string): Promise<WarmPoolActionResponse> => {
    return apiClient.post(`${WARM_POOLS_API_BASE}/${name}/resume`);
  },

  /**
   * Change a warm node pool's mode (default <-> overflow). Re-attaches all
   * attached clusters in the new mode and updates the pool's mode label.
   */
  changeMode: (name: string, mode: WarmPoolMode): Promise<WarmPoolActionResponse> => {
    return apiClient.post(`${WARM_POOLS_API_BASE}/${name}/mode`, { mode });
  },
};

export default warmPoolsApi;
